/**
 * Azure Voice Live WebSocket client service
 */

import { 
  VoiceLiveEvent, 
  VoiceLiveConnectionConfig, 
  VoiceLiveConnectionState, 
  VoiceLiveConversationState,
  VoiceLiveError,
  SessionUpdateEvent,
  InputAudioBufferAppendEvent,
  // InputAudioBufferCommitEvent,
  ResponseAudioDeltaEvent,
  ResponseAudioTranscriptDeltaEvent,
  ConversationItemInputAudioTranscriptionCompletedEvent,
  InputAudioBufferSpeechStartedEvent,
  InputAudioBufferSpeechStoppedEvent,
  ResponseCreatedEvent,
  ResponseDoneEvent
} from '../types/voiceLive.types';
import { logger } from '../utils/logger';
import { postJson, API_ENDPOINTS, getApiUrl } from '../utils/apiUtils';

export class VoiceLiveClient {
  private websocket: WebSocket | null = null;
  private config: VoiceLiveConnectionConfig | null = null;
  private connectionState: VoiceLiveConnectionState = 'disconnected';
  private conversationState: VoiceLiveConversationState = 'idle';
  private eventListeners: Map<string, Function[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private userAge?: number;
  private currentStoryId?: string;
  private currentStoryContent?: string;

  // Audio buffer management
  private audioBufferStartTime: number | null = null;
  private audioChunkCount = 0;
  private readonly MINIMUM_AUDIO_DURATION_MS = 150; // Slightly above 100ms requirement for safety
  private readonly SAMPLE_RATE = 24000; // 24kHz as configured in audioUtils
  private readonly CHUNK_DURATION_MS = 170; // Approximate duration per 4096 sample chunk at 24kHz

  // Continuous listening management
  private continuousAudioRecorder: any = null;
  private isContinuousListening = false;
  
  // User transcription tracking
  private awaitingUserTranscription: string | null = null;
  private transcriptionTimeout: number | null = null;
  
  // ✅ CONSOLIDATED Echo cancellation - single source of truth for mic muting
  private micMutedForAssistant = false;  // Consolidated flag (replaced two separate flags)
  
  // ✅ Audio playback tracking - reference to external audio processor
  private audioProcessor: any = null; // Reference set by App.tsx

  // Event handling
  addEventListener(eventType: string, callback: Function): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)?.push(callback);
  }

  removeEventListener(eventType: string, callback: Function): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  // Set user context for topic monitoring
  setUserContext(userAge: number, storyId?: string, storyContent?: string): void {
    this.userAge = userAge;
    this.currentStoryId = storyId;
    this.currentStoryContent = storyContent;
  }

  // Send message to backend for topic monitoring
  private async sendMessageToBackend(messageType: 'user' | 'assistant', content: string, messageId: string): Promise<void> {
    if (!this.config?.conversation_id || !this.userAge) {
      return; // Skip if no context available
    }

    try {
      const response = await postJson(API_ENDPOINTS.UI_STATE_VOICELIVE_MESSAGE, {
        conversationId: this.config.conversation_id,
        userAge: this.userAge,
        storyId: this.currentStoryId,
        messageType,
        messageContent: content,
        messageId
      });

      if (!response.ok) {
        console.warn('Failed to send message to backend for topic monitoring:', response.status);
      } else {
        const result = await response.json();
        console.log('Message sent to backend:', result);
      }
    } catch (error) {
      console.warn('Error sending message to backend:', error);
    }
  }

  private emit(eventType: string, ...args: any[]): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          logger.logErrorEvent('event_listener_error', error as Error, {
            eventType,
            args
          }, undefined, this.config?.conversation_id);
        }
      });
    }
  }

  // Connection management
  async connect(config: VoiceLiveConnectionConfig): Promise<void> {
    // Close any existing connection first
    if (this.websocket) {
      if (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING) {
        logger.logConnectionEvent('closing_existing_connection', {
          current_state: this.websocket.readyState,
          conversation_id: this.config?.conversation_id
        }, undefined, config.conversation_id);
        this.websocket.close();
      }
      this.websocket = null;
    }

    // Reset reconnection attempts when manually connecting
    if (this.connectionState === 'error' || this.connectionState === 'disconnected') {
      this.reconnectAttempts = 0;
    }

    this.config = config;
    this.connectionState = 'connecting';
    this.emit('connectionStateChange', this.connectionState);

    logger.logConnectionEvent('connecting', {
      websocket_url_length: config.websocket_url.length,
      conversation_id: config.conversation_id
    }, undefined, config.conversation_id);

    try {
      // Use backend WebSocket proxy - derive URL from environment or API URL
      const backendWsUrl = `${
        process.env.REACT_APP_API_WEBSOCKET_URL || 
        process.env.REACT_APP_API_URL?.replace('http', 'ws').replace('https', 'wss') || 
        (window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + window.location.host
      }/voicelive/ws/${config.conversation_id}`;
      
      logger.logVoiceLiveEvent('using_backend_websocket_proxy', {
        conversation_id: config.conversation_id,
        backend_websocket_url: backendWsUrl,
        server_side_control: true
      }, this.userAge, config.conversation_id);

      this.websocket = new WebSocket(backendWsUrl);
      this.setupWebSocketHandlers();
    } catch (error) {
      this.handleConnectionError(error as Error);
      throw error;
    }
  }

  private setupWebSocketHandlers(): void {
    if (!this.websocket || !this.config) return;

    this.websocket.onopen = () => {
      logger.logConnectionEvent('connected', {}, undefined, this.config?.conversation_id);
      this.connectionState = 'connected';
      this.reconnectAttempts = 0;
      this.emit('connectionStateChange', this.connectionState);
      
      // Send initial session configuration
      this.sendSessionUpdate();
    };

    this.websocket.onmessage = (event) => {
      try {
        const data: VoiceLiveEvent = JSON.parse(event.data);
        
        // Log only important events, not every delta
        const importantEvents = ['conversation.item.created', 'response.created', 'response.done', 'reading_progress', 'error'];
        if (importantEvents.includes(data.type)) {
          console.log('📨 WebSocket event:', data.type);
        }
        
        // Handle reading progress updates
        if (data.type === 'reading_progress') {
          console.log('📖 Reading progress update received:', data);
          this.emit('readingProgress', data);
        }
        
        // Handle quiz question display events
        if (data.type === 'quiz_question_display') {
          console.log('🎮 Quiz question display event received:', data);
          this.emit('quizQuestionDisplay', data);
        }
        
        // Handle persona switch events
        if (data.type === 'persona_switched') {
          console.log('🎭 Persona switched event received:', data);
          this.emit('personaSwitched', data);
        }
        
        // Handle persona returned events (fallback for older format)
        if (data.type === 'persona_returned') {
          console.log('🎭 Persona returned event received:', data);
          this.emit('personaReturned', data);
        }
        
        // Handle pronunciation aggregate updates (event-driven, replaces polling)
        if (data.type === 'pronunciation_aggregate_update') {
          console.log('📊 Pronunciation aggregate update received:', data);
          this.emit('pronunciationAggregateUpdate', data);
        }
        
        // Only log final transcript completion, not every delta
        if (data.type === 'response.audio_transcript.done') {
          console.log('✅ Transcript complete:', (data as any).transcript);
        }
        
        this.handleVoiceLiveEvent(data);
      } catch (error) {
        console.error('❌ Failed to parse WebSocket message:', error);
        console.error('   Raw message data:', event.data);
        logger.logErrorEvent('message_parse_error', error as Error, {
          data: event.data
        }, undefined, this.config?.conversation_id);
      }
    };

    this.websocket.onerror = (error) => {
      console.error('🔴 WebSocket error event:', {
        type: error.type,
        target: error.target instanceof WebSocket ? {
          url: (error.target as WebSocket).url,
          readyState: (error.target as WebSocket).readyState,
          readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][(error.target as WebSocket).readyState]
        } : 'unknown',
        timestamp: new Date().toISOString()
      });
      
      logger.logConnectionEvent('websocket_error', {
        error: error,
        error_type: error.type,
        websocket_state: this.websocket?.readyState,
        conversation_id: this.config?.conversation_id
      }, undefined, this.config?.conversation_id);
      
      this.handleConnectionError(new Error(`WebSocket error: ${error.type}`));
    };

    this.websocket.onclose = (event) => {
      logger.logConnectionEvent('disconnected', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      }, undefined, this.config?.conversation_id);
      
      this.connectionState = 'disconnected';
      this.conversationState = 'idle';
      this.emit('connectionStateChange', this.connectionState);
      
      // Attempt reconnection if not intentionally closed
      if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnection();
      }
    };
  }

  private sendSessionUpdate(): void {
    if (!this.config) return;

    const sessionUpdateEvent: SessionUpdateEvent = {
      type: 'session.update',
      session: this.config.session_config
    };

    this.sendEvent(sessionUpdateEvent);
    logger.logVoiceLiveEvent('session_update_sent', {
      voice_name: this.config.session_config.voice,
      turn_detection_type: this.config.session_config.turn_detection?.type,
      modalities: this.config.session_config.modalities,
      session_config: 'updated'
    }, undefined, this.config.conversation_id);
  }

  private handleVoiceLiveEvent(event: VoiceLiveEvent): void {
    logger.logVoiceLiveEvent('received_event', {
      type: event.type,
      data_size: JSON.stringify(event).length
    }, undefined, this.config?.conversation_id);

    switch (event.type) {
      case 'response.audio.delta':
        this.handleResponseAudioDelta(event as ResponseAudioDeltaEvent);
        break;
      case 'response.audio_transcript.delta':
        this.handleResponseAudioTranscriptDelta(event as ResponseAudioTranscriptDeltaEvent);
        break;
      case 'conversation.item.input_audio_transcription.completed':
        this.handleInputAudioTranscriptionCompleted(event as ConversationItemInputAudioTranscriptionCompletedEvent);
        break;
      case 'input_audio_buffer.speech_started':
        this.handleSpeechStarted(event as InputAudioBufferSpeechStartedEvent);
        break;
      case 'input_audio_buffer.speech_stopped':
        this.handleSpeechStopped(event as InputAudioBufferSpeechStoppedEvent);
        break;
      case 'response.created':
        this.handleResponseCreated(event as ResponseCreatedEvent);
        break;
      case 'response.done':
        this.handleResponseDone(event as ResponseDoneEvent);
        break;      
      case 'assistant_speech_started':
        this.handleAssistantSpeechStarted(event);
        break;
      case 'assistant_speech_ended':
        this.handleAssistantSpeechEnded(event);
        break;
      case 'assistant_started_speaking':
        this.handleAssistantStartedSpeaking();
        break;
      case 'assistant_stopped_speaking':
        this.handleAssistantStoppedSpeaking();
        break;
      case 'assistant_audio_transmission_complete':
        this.handleAudioTransmissionComplete(event);
        break;
      case 'error':
        // Handle error events with detailed logging
        const errorEvent = event as any;
        console.error('❌ VoiceLive error event received:', {
          type: errorEvent.type,
          error: errorEvent.error,
          code: errorEvent.code,
          message: errorEvent.message,
          details: errorEvent
        });
        logger.logErrorEvent('voicelive_error', new Error(errorEvent.message || errorEvent.error || 'Unknown VoiceLive error'), {
          error_code: errorEvent.code,
          error_type: errorEvent.error,
          full_event: errorEvent
        }, this.userAge, this.config?.conversation_id);
        this.emit('voiceLiveError', errorEvent);
        break;
      default:
        // Forward all events to listeners
        this.emit(`voiceLiveEvent:${event.type}`, event);
        break;
    }

    // Also emit the raw event
    this.emit('voiceLiveEvent', event);
  }

  private handleResponseAudioDelta(event: ResponseAudioDeltaEvent): void {
    this.conversationState = 'speaking';
    this.emit('conversationStateChange', this.conversationState);
    this.emit('audioResponse', event.delta);
  }
  
  private handleResponseDone(event: ResponseDoneEvent): void {
    this.conversationState = 'idle';
    this.emit('conversationStateChange', this.conversationState);
    this.emit('responseCompleted', event.response.id);
    
    console.log('✅ VoiceLive response completed - AI finished speaking');
    
    // Note: Turn detection is managed by backend via VAD API endpoints
    // No need to manually re-enable turn detection here
    
    // Send complete assistant response to backend for topic monitoring
    // We'll accumulate the response text from transcript deltas and send when complete
    this.sendAssistantResponseWhenComplete(event.response.id);
  }
  
  private assistantResponseTexts: Map<string, string> = new Map();
   
  private handleResponseAudioTranscriptDelta(event: ResponseAudioTranscriptDeltaEvent): void {
    this.emit('assistantTranscriptDelta', event.delta, event.item_id);
    
    // Accumulate assistant response text
    const currentText = this.assistantResponseTexts.get(event.item_id) || '';
    this.assistantResponseTexts.set(event.item_id, currentText + event.delta);
  }
  
  /**
   * ✅ CONSOLIDATED ECHO CANCELLATION - Handle assistant_started_speaking event from backend
   */
  private handleAssistantStartedSpeaking(): void {
    this.micMutedForAssistant = false;
    console.log('🔇 [PRE-EMPTIVE] Muting mic - assistant response starting');
    this.emit('micMutedStateChange', true);
  }
  
  /**
   * ✅ CONSOLIDATED ECHO CANCELLATION - Handle assistant_stopped_speaking event from backend
   */
  private handleAssistantStoppedSpeaking(): void {
    this.micMutedForAssistant = false;
    console.log('🎤 Unmuting mic - audio playback confirmed complete');
    this.emit('micMutedStateChange', false);
  }
  
  /**
   * ✅ DEPRECATED - Kept for backwards compatibility
   * Handle backend notification that assistant started speaking
   */
  private handleAssistantSpeechStarted(event: any): void {
    console.log(`🔇 [${new Date().toISOString()}] Assistant started speaking (legacy event)`);
    
    this.micMutedForAssistant = true;
    
    // Emit event for UI updates (e.g., show mic muted indicator)
    this.emit('micMutedForAssistant', true);
    
    logger.logVoiceLiveEvent('assistant_speech_started', {
      reason: event.reason || 'echo_cancellation',
      continuous_listening: this.isContinuousListening
    }, this.userAge, this.config?.conversation_id);
  }
  
  /**
   * ✅ DEPRECATED - Kept for backwards compatibility
   * Handle backend notification that assistant finished speaking
   */
  private handleAssistantSpeechEnded(event: any): void {
    console.log(`🔊 [${new Date().toISOString()}] Assistant finished speaking (legacy event)`);
    
    this.micMutedForAssistant = false;
    
    // Emit event for UI updates
    this.emit('micMutedForAssistant', false);
    
    logger.logVoiceLiveEvent('assistant_speech_ended', {
      reason: event.reason || 'echo_cancellation',
      continuous_listening: this.isContinuousListening
    }, this.userAge, this.config?.conversation_id);
  }
  
  /**
   * ✅ NEW: Handle audio transmission complete - wait for playback to finish
   */
  private handleAudioTransmissionComplete(event: any): void {
    console.log(`📤 [${new Date().toISOString()}] Audio transmission complete, waiting for playback...`);
    
    // Audio chunks have been sent, but playback may still be happening
    // Monitor the audio player to detect when queue is empty and playback finishes
    this.monitorAudioPlaybackCompletion();
    
    logger.logVoiceLiveEvent('audio_transmission_complete', {
      reason: event.reason || 'awaiting_playback',
      continuous_listening: this.isContinuousListening
    }, this.userAge, this.config?.conversation_id);
  }
  
  /**
   * ✅ NEW: Monitor audio playback queue and notify backend when playback completes
   */
  private monitorAudioPlaybackCompletion(): void {
    // Check audio player state every 100ms
    const checkInterval = setInterval(() => {
      // Check if audio processor is still playing
      const isPlaying = this.audioProcessor?.isCurrentlyPlaying() || false;
      
      if (!isPlaying) {
        // Playback complete! Notify backend
        clearInterval(checkInterval);
        
        console.log(`✅ [${new Date().toISOString()}] Audio playback COMPLETE - notifying backend`);
        
        // Send playback completion event to backend
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.websocket.send(JSON.stringify({
            type: 'audio_playback_complete',
            conversation_id: this.config?.conversation_id,
            timestamp: new Date().toISOString()
          }));
        }
        
        logger.logVoiceLiveEvent('audio_playback_complete', {
          continuous_listening: this.isContinuousListening
        }, this.userAge, this.config?.conversation_id);
      }
    }, 100); // Check every 100ms
    
    // Safety timeout: force completion after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      console.warn(`⏰ [${new Date().toISOString()}] Audio playback timeout - forcing completion`);
      
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({
          type: 'audio_playback_complete',
          conversation_id: this.config?.conversation_id,
          timestamp: new Date().toISOString(),
          forced: true
        }));
      }
    }, 10000); // 10 second timeout
  }
  
  /**
   * Set audio processor reference for playback completion tracking
   */
  setAudioProcessor(processor: any): void {
    this.audioProcessor = processor;
  }
  
  private sendAssistantResponseWhenComplete(responseId: string): void {
    // Find the accumulated response text for this response
    const responseText = this.assistantResponseTexts.get(responseId);
    if (responseText && responseText.trim()) {
      this.sendMessageToBackend('assistant', responseText, responseId);
      // Clean up stored text
      this.assistantResponseTexts.delete(responseId);
    }
  }

  private handleInputAudioTranscriptionCompleted(event: ConversationItemInputAudioTranscriptionCompletedEvent): void {
    console.log('🎉 USER TRANSCRIPTION COMPLETED IN VOICELIVECLIENT:');
    console.log('   📝 Transcript:', event.transcript);
    console.log('   🆔 Item ID:', event.item_id);
    console.log('   📦 Full event:', event);
    console.log('   🔤 Transcript length:', event.transcript?.length || 0);
    console.log('   ✅ About to emit userTranscriptionCompleted to App.tsx...');
    
    this.emit('userTranscriptionCompleted', event.transcript, event.item_id);
    console.log('   ✅ Event emitted successfully!');
    
    // ============================================================
    // TRIGGER PRONUNCIATION ASSESSMENT WITH TRANSCRIPTION
    // ============================================================
    // Use the actual transcription as reference text - this avoids "Omission" errors
    if (event.transcript && event.transcript.trim()) {
      console.log('   📊 Triggering pronunciation assessment with transcription as reference...');
      this.triggerTranscriptionBasedAssessment(event.item_id, event.transcript).catch(error => {
        console.warn('⚠️ Failed to trigger transcription-based assessment:', error);
      });
    }
    
    // Send user message to backend for topic monitoring
    if (event.transcript && event.transcript.trim()) {
      console.log('   📤 Sending transcript to backend for topic monitoring...');
      this.sendMessageToBackend('user', event.transcript, event.item_id);
    } else {
      console.warn('   ⚠️ No transcript content to emit or send to backend');
      console.warn('   🔍 Checking alternative transcript fields...');
      const eventAsAny = event as any;
      for (const field of ['text', 'content', 'transcription']) {
        if (eventAsAny[field]) {
          console.log(`   🔄 Found content in '${field}' field: ${eventAsAny[field]}`);
          // Use alternative field and emit again
          this.emit('userTranscriptionCompleted', eventAsAny[field], event.item_id);
          break;
        }
      }
    }
    
    // NOW trigger response creation after user transcription is completed
    if (this.awaitingUserTranscription === event.item_id && this.isContinuousListening) {
      console.log('✅ User transcription completed - now triggering response creation...');
      
      // Clear timeout and waiting state
      if (this.transcriptionTimeout) {
        clearTimeout(this.transcriptionTimeout);
        this.transcriptionTimeout = null;
      }
      this.awaitingUserTranscription = null;
      
      this.createResponseAfterSpeech();
    }
  }

  private handleSpeechStarted(event: InputAudioBufferSpeechStartedEvent): void {
    this.conversationState = 'listening';
    this.emit('conversationStateChange', this.conversationState);
    this.emit('speechStarted', event.item_id);
    
    console.log('🎤 VoiceLive detected speech started:', event.item_id);
    logger.logVoiceLiveEvent('speech_detection_started', {
      item_id: event.item_id,
      continuous_mode: this.isContinuousListening
     }, this.userAge, this.config?.conversation_id);
  }

  private handleSpeechStopped(event: InputAudioBufferSpeechStoppedEvent): void {
    this.conversationState = 'processing';
    this.emit('conversationStateChange', this.conversationState);
    this.emit('speechStopped', event.item_id);
    
    console.log('🛑 VoiceLive detected speech stopped:', event.item_id);
    logger.logVoiceLiveEvent('speech_detection_stopped', {
      item_id: event.item_id,
      continuous_mode: this.isContinuousListening
    }, this.userAge, this.config?.conversation_id);
    
    // NOTE: Pronunciation assessment is now triggered AFTER transcription completes
    // See handleInputAudioTranscriptionCompleted() for the new flow
    
    // Wait for user transcription completion before triggering response
    if (this.isContinuousListening) {
      console.log('⏳ Speech stopped - waiting for user transcription completion before response...');
      this.awaitingUserTranscription = event.item_id;
      
      // Set timeout fallback in case transcription never arrives (5 seconds)
      if (this.transcriptionTimeout) {
        clearTimeout(this.transcriptionTimeout);
      }
      this.transcriptionTimeout = window.setTimeout(() => {
        if (this.awaitingUserTranscription === event.item_id) {
          console.warn('⚠️ Transcription timeout - proceeding with response creation without user transcript');
          this.awaitingUserTranscription = null;
          this.createResponseAfterSpeech();
        }
      }, 5000);
      
      // Note: Response will be triggered in handleInputAudioTranscriptionCompleted() or timeout
    }
  }

  /**
   * Trigger pronunciation assessment using the user's transcription as reference text.
   * This solves the "Omission" error problem by using what was actually spoken.
   */
  private async triggerTranscriptionBasedAssessment(itemId: string, transcription: string): Promise<void> {
    if (!this.config?.conversation_id) {
      console.warn('⚠️ No conversation ID available for pronunciation assessment');
      return;
    }

    try {
      console.log(`🎯 Triggering per-segment pronunciation assessment for item ${itemId}`);
      console.log(`   📝 Transcription: "${transcription}"`);
      console.log(`   🆔 Conversation ID: ${this.config.conversation_id}`);
      
      const response = await fetch(getApiUrl('/pronunciation/speech_stopped'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: this.config.conversation_id,
          session_id: this.config.conversation_id,
          item_id: itemId,
          reference_text: transcription,  // Use transcription, NOT full story
          user_age: this.userAge
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.assessed) {
          console.log(
            `✅ Speech segment assessed with transcription - Accuracy: ${result.assessment?.accuracy_score}%`,
            result.assessment?.pronunciation_coach_triggered ? '🎓 Pronunciation Coach triggered!' : ''
          );
          
          if (result.assessment?.pronunciation_coach_triggered) {
            logger.logVoiceLiveEvent('pronunciation_coach_auto_triggered', {
              item_id: itemId,
              difficult_word: result.assessment.difficult_word,
              accuracy: result.assessment.accuracy_score,
              assessment_id: result.assessment.assessment_id,
              transcription_length: transcription.length
            }, this.userAge, this.config.conversation_id);
          }
        } else {
          console.log(`ℹ️ Segment not assessed: ${result.reason}`);
        }
      } else {
        console.warn('⚠️ Pronunciation assessment endpoint returned error:', response.status);
      }
    } catch (error) {
      console.error('❌ Failed to trigger transcription-based assessment:', error);
      // Don't throw - this is a non-critical enhancement
    }
  }

  /**
   * @deprecated Use triggerTranscriptionBasedAssessment instead
   * Trigger pronunciation assessment for the current speech segment.
   * This enables per-segment assessment rather than waiting for mic to stop.
   */
  private async triggerSegmentPronunciationAssessment(itemId: string): Promise<void> {
    if (!this.config?.conversation_id) {
      console.warn('⚠️ No conversation ID available for pronunciation assessment');
      return;
    }

    try {
      const response = await fetch(getApiUrl('/pronunciation/speech_stopped'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: this.config.conversation_id,
          session_id: this.config.conversation_id,
          item_id: itemId,
          reference_text: this.currentStoryContent || null,
          user_age: this.userAge
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.assessed) {
          console.log(
            `✅ Speech segment assessed automatically - Accuracy: ${result.assessment?.accuracy_score}%`,
            result.assessment?.pronunciation_coach_triggered ? '🎓 Pronunciation Coach triggered!' : ''
          );
          
          if (result.assessment?.pronunciation_coach_triggered) {
            logger.logVoiceLiveEvent('pronunciation_coach_auto_triggered', {
              item_id: itemId,
              difficult_word: result.assessment.difficult_word,
              accuracy: result.assessment.accuracy_score,
              assessment_id: result.assessment.assessment_id
            }, this.userAge, this.config.conversation_id);
          }
        } else {
          console.log(`ℹ️ Segment not assessed: ${result.reason}`);
        }
      } else {
        console.warn('⚠️ Pronunciation assessment endpoint returned error:', response.status);
      }
    } catch (error) {
      console.error('❌ Failed to trigger segment pronunciation assessment:', error);
      // Don't throw - this is a non-critical enhancement
    }
  }

  private createResponseAfterSpeech(): void {
    if (!this.isConnected()) {
      console.warn('❌ Cannot create response: VoiceLive not connected');
      return;
    }

    // Create a response to trigger the assistant
    const responseCreateEvent = {
      type: 'response.create',
      response: {
        modalities: ['text', 'audio']
      }
    };

    this.sendEvent(responseCreateEvent);
    console.log('📤 Sent response.create event to VoiceLive');
    
    logger.logVoiceLiveEvent('response_create_triggered', {
      trigger: 'speech_stopped',
      continuous_mode: this.isContinuousListening,
      user_age: this.userAge,
      story_id: this.currentStoryId
    }, this.userAge, this.config?.conversation_id);
  }

  private handleResponseCreated(event: ResponseCreatedEvent): void {
    this.conversationState = 'processing';
    this.emit('conversationStateChange', this.conversationState);
    this.emit('responseStarted', event.response.id);
    
    console.log('🤖 VoiceLive response created:', event.response.id);
    logger.logVoiceLiveEvent('response_created_received', {
      response_id: event.response.id,
      continuous_mode: this.isContinuousListening
    }, this.userAge, this.config?.conversation_id);
  }

  // Audio streaming
  sendAudioData(base64AudioData: string): void {
    if (!this.isConnected()) {
      logger.logAudioEvent('send_audio_failed_not_connected', {
        data_length: base64AudioData.length
      }, undefined, this.config?.conversation_id);
      return;
    }
    
    // ✅ CONSOLIDATED ECHO CANCELLATION - Block audio when assistant is speaking
    if (this.micMutedForAssistant) {
      logger.logAudioEventDEBUG('audio_blocked_assistant_speaking', {
        data_length: base64AudioData.length
      }, this.userAge, this.config?.conversation_id);
      return;
    }

    // Track audio buffer timing
    if (this.audioBufferStartTime === null) {
      this.audioBufferStartTime = Date.now();
      this.audioChunkCount = 0;
    }
    this.audioChunkCount++;

    const event: InputAudioBufferAppendEvent = {
      type: 'input_audio_buffer.append',
      audio: base64AudioData
    };

    this.sendEvent(event);
    
    // Log buffer progress for debugging (DEBUG level to reduce console noise)
    const currentDuration = this.getEstimatedAudioDuration();
    logger.debug('AUDIO', 'audio_buffer_append', {
      chunk_count: this.audioChunkCount,
      estimated_duration_ms: currentDuration,
      data_length: base64AudioData.length
    }, undefined, this.config?.conversation_id);
  }

  private getEstimatedAudioDuration(): number {
    if (this.audioBufferStartTime === null) return 0;
    return Date.now() - this.audioBufferStartTime;
  }

  private hasMinimumAudioData(): boolean {
    const estimatedDuration = this.getEstimatedAudioDuration();
    return estimatedDuration >= this.MINIMUM_AUDIO_DURATION_MS && this.audioChunkCount > 0;
  }

  private resetAudioBuffer(): void {
    this.audioBufferStartTime = null;
    this.audioChunkCount = 0;
  }

  /**
   * @deprecated This method is not compatible with Azure Semantic VAD
   * Azure Semantic VAD automatically commits audio when speech end is detected.
   * Manual commit using input_audio_buffer.commit is not supported.
   * Keeping method for backward compatibility but it does nothing.
   */
  commitAudioBuffer(): void {
    // DISABLED: Not compatible with Azure Semantic VAD
    // Azure automatically commits audio when speech ends
    console.log('⚠️ commitAudioBuffer called but disabled - Azure Semantic VAD handles commits automatically');
    
    // Just reset the buffer tracking
    this.resetAudioBuffer();
  }

  // Pending configuration updates for batching
  private pendingConfigUpdates: Partial<any> | null = null;
  private configUpdateTimer: number | null = null;
  private readonly CONFIG_UPDATE_DEBOUNCE_MS = 300;

  // Update session configuration dynamically with batching and debouncing
  updateSessionConfig(updates: Partial<any>, immediate: boolean = false): void {
    if (!this.isConnected() || !this.config) return;
    
    // Batch updates to reduce WebSocket traffic
    this.pendingConfigUpdates = {
      ...(this.pendingConfigUpdates || {}),
      ...updates
    };
    
    // Clear existing timer
    if (this.configUpdateTimer) {
      window.clearTimeout(this.configUpdateTimer);
      this.configUpdateTimer = null;
    }
    
    // Send immediately if requested, otherwise debounce
    if (immediate) {
      this.flushPendingConfigUpdates();
    } else {
      this.configUpdateTimer = window.setTimeout(() => {
        this.flushPendingConfigUpdates();
      }, this.CONFIG_UPDATE_DEBOUNCE_MS);
    }
  }
  
  private flushPendingConfigUpdates(): void {
    if (!this.pendingConfigUpdates || !this.config) return;
    
    // Check if configuration actually changed
    const currentConfig = this.config.session_config as any;
    const hasChanges = Object.entries(this.pendingConfigUpdates).some(([key, value]) => {
      return JSON.stringify(currentConfig[key]) !== JSON.stringify(value);
    });
    
    if (!hasChanges) {
      this.pendingConfigUpdates = null;
      return;
    }
    
    // Update the stored configuration
    this.config.session_config = {
      ...this.config.session_config,
      ...this.pendingConfigUpdates
    };
    
    const updatedFields = Object.keys(this.pendingConfigUpdates);
    this.pendingConfigUpdates = null;
    
    // Send session update to VoiceLive
    this.sendSessionUpdate();
    
    logger.logVoiceLiveEvent('session_config_updated', {
      updated_fields: updatedFields
    }, undefined, this.config.conversation_id);
  }

  // Get current session configuration
  getSessionConfig(): any {
    return this.config?.session_config;
  }

  // Utility methods
  public sendVoiceLiveEvent(event: VoiceLiveEvent): void {
    this.sendEvent(event);
  }

  private sendEvent(event: VoiceLiveEvent): void {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      logger.logVoiceLiveEvent('send_event_failed', {
        event_type: event.type,
        websocket_state: this.websocket?.readyState
      }, undefined, this.config?.conversation_id);
      return;
    }

    try {
      this.websocket.send(JSON.stringify(event));
    } catch (error) {
      logger.logErrorEvent('send_event_error', error as Error, {
        event_type: event.type
      }, undefined, this.config?.conversation_id);
    }
  }

  private handleConnectionError(error: Error): void {
    this.connectionState = 'error';
    this.conversationState = 'error';
    
    const voiceLiveError: VoiceLiveError = {
      type: 'connection_error',
      message: error.message,
      timestamp: new Date()
    };

    logger.logErrorEvent('connection_error', error, {}, undefined, this.config?.conversation_id);
    this.emit('connectionStateChange', this.connectionState);
    this.emit('error', voiceLiveError);
  }

  private async attemptReconnection(): Promise<void> {
    if (!this.config) return;

    this.reconnectAttempts++;
    this.connectionState = 'reconnecting';
    this.emit('connectionStateChange', this.connectionState);

    logger.logConnectionEvent('reconnection_attempt', {
      attempt: this.reconnectAttempts,
      max_attempts: this.maxReconnectAttempts
    }, undefined, this.config.conversation_id);

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s
    const backoffDelay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    await new Promise(resolve => setTimeout(resolve, backoffDelay));

    try {
      await this.connect(this.config);
    } catch (error) {
      logger.logErrorEvent('reconnection_failed', error as Error, {
        attempt: this.reconnectAttempts,
        backoff_delay: backoffDelay
      }, undefined, this.config.conversation_id);
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.connectionState = 'error';
        this.emit('connectionStateChange', this.connectionState);
        this.handleConnectionError(new Error('Max reconnection attempts reached'));
      }
    }
  }

  // Status methods
  isConnected(): boolean {
    return this.websocket !== null && 
           this.websocket.readyState === WebSocket.OPEN &&
           this.connectionState === 'connected';
  }

  getConnectionState(): VoiceLiveConnectionState {
    return this.connectionState;
  }

  getConversationState(): VoiceLiveConversationState {
    return this.conversationState;
  }

  // Automatic introduction trigger
  triggerStoryIntroduction(): void {
    if (!this.isConnected()) {
      console.warn('Cannot trigger introduction: VoiceLive not connected');
      return;
    }

    console.log('🎯 Triggering story introduction');

    // Note: Backend handles announcement VAD configuration via start_welcome_announcement endpoint
    // Frontend just triggers the conversation flow

    // Create a conversation item with a simple greeting to trigger the assistant's story introduction
    const conversationItem = {
      type: 'conversation.item.create',
      item: {
        id: `item_${Date.now()}`,
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: 'Hi!'
          }
        ]
      }
    };

    // Send the conversation item to trigger assistant response
    this.sendEvent(conversationItem);

    // Create a response to trigger the assistant
    const responseCreateEvent = {
      type: 'response.create',
      response: {
        modalities: ['text', 'audio']
      }
    };

    this.sendEvent(responseCreateEvent);

    logger.logVoiceLiveEvent('story_introduction_triggered', {
      trigger_method: 'conversation_item_create',
      user_age: this.userAge,
      story_id: this.currentStoryId
    }, this.userAge, this.config?.conversation_id);
  }

  // Custom announcement trigger with specific message
  triggerCustomAnnouncement(message: string, announcementType: string = 'custom'): void {
    if (!this.isConnected()) {
      console.warn('Cannot trigger custom announcement: VoiceLive not connected');
      return;
    }

    console.log(`🎯 Triggering custom announcement (${announcementType}): ${message.substring(0, 50)}...`);

    // Create a conversation item with custom message
    const conversationItem = {
      type: 'conversation.item.create',
      item: {
        id: `item_${Date.now()}`,
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: message
          }
        ]
      }
    };

    // Send the conversation item to trigger assistant response
    this.sendEvent(conversationItem);

    // Create a response to trigger the assistant
    const responseCreateEvent = {
      type: 'response.create',
      response: {
        modalities: ['text', 'audio']
      }
    };

    this.sendEvent(responseCreateEvent);

    logger.logVoiceLiveEvent('story_introduction_triggered', {
      trigger_method: 'conversation_item_create',
      user_age: this.userAge,
      story_id: this.currentStoryId
    }, this.userAge, this.config?.conversation_id);
  }

  // Disable reading mode and return to conversation mode
  async disableReadingMode(): Promise<void> {
    if (!this.isConnected() || !this.config) {
      return;
    }

    // Return to normal child-friendly conversation mode via backend API
    await this.enableChildFriendlyMode();
    
    console.log('💬 Reading mode disabled - returning to conversation mode');
    logger.logVoiceLiveEvent('reading_mode_disabled', {
      user_age: this.userAge,
      story_id: this.currentStoryId
    }, this.userAge, this.config?.conversation_id);
  }
  async enableChildFriendlyMode(): Promise<void> {
    if (!this.isConnected() || !this.config) {
      console.warn('Cannot enable child-friendly mode: VoiceLive not connected');
      return;
    }

    try {
      // Call backend API to enable conversation mode (child-friendly)
      const response = await postJson(
        `/voicelive/vad/enable-conversation-mode/${this.config.conversation_id}`,
        {}
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to enable child-friendly mode:', errorData);
        return;
      }

      const result = await response.json();
      console.log(`🔧 Child-friendly mode enabled:`, result);
      console.log(`   - VAD Mode: ${result.mode}`);
      console.log(`   - Silence Duration: ${result.vad_config.silence_duration_ms}ms (age ${this.userAge})`);
      console.log(`   - Threshold: ${result.vad_config.threshold}`);
      console.log(`   - Prefix Padding: ${result.vad_config.prefix_padding_ms}ms`);

      logger.logVoiceLiveEvent('child_friendly_mode_enabled', {
        user_age: this.userAge,
        story_id: this.currentStoryId,
        vad_mode: result.mode,
        silence_duration_ms: result.vad_config.silence_duration_ms,
        threshold: result.vad_config.threshold,
        turn_detection_updated: true
      }, this.userAge, this.config.conversation_id);
    } catch (error) {
      console.error('Error enabling child-friendly mode:', error);
    }
  }

  // Enable reading mode with extra patience for natural reading pauses
  async enableReadingMode(): Promise<void> {
    if (!this.isConnected() || !this.config) {
      console.warn('Cannot enable reading mode: VoiceLive not connected');
      return;
    }

    try {
      // Call backend API to enable reading mode
      const response = await postJson(
        `/voicelive/vad/enable-reading-mode/${this.config.conversation_id}`,
        {}
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to enable reading mode:', errorData);
        return;
      }

      const result = await response.json();
      console.log('📖 Reading mode enabled:', result);
      console.log(`   - VAD Mode: ${result.mode}`);
      console.log(`   - Silence Duration: ${result.vad_config.silence_duration_ms}ms (patient for reading pauses)`);
      console.log(`   - Threshold: ${result.vad_config.threshold}`);
      console.log(`   - Prefix Padding: ${result.vad_config.prefix_padding_ms}ms`);

      logger.logVoiceLiveEvent('reading_mode_enabled', {
        user_age: this.userAge,
        story_id: this.currentStoryId,
        vad_mode: result.mode,
        silence_duration_ms: result.vad_config.silence_duration_ms,
        threshold: result.vad_config.threshold
      }, this.userAge, this.config?.conversation_id);
    } catch (error) {
      console.error('Error enabling reading mode:', error);
    }
  }

  // Continuous listening mode
  async startContinuousListening(): Promise<void> {
    if (this.isContinuousListening || !this.isConnected()) {
      return;
    }

    try {
      // Import createAudioRecorder dynamically to avoid circular dependency
      const { createAudioRecorder } = await import('../utils/audioUtils');
      
      this.continuousAudioRecorder = await createAudioRecorder({
        onDataAvailable: (base64Data: string) => {
          // In continuous mode, just stream audio - server VAD will handle speech detection
          this.sendAudioData(base64Data);
        },
        onStop: () => {
          // In continuous mode, don't commit buffers - server VAD handles this
          // Only reset our local tracking
          this.resetAudioBuffer();
        }
      });

      this.continuousAudioRecorder.start();
      this.isContinuousListening = true;
      
      console.log('🎙️ Continuous listening started - audio streaming to VoiceLive with server VAD');
      logger.logAudioEvent('continuous_listening_started', {}, this.userAge, this.config?.conversation_id);
      this.emit('continuousListeningStateChange', true);
      
    } catch (error) {
      logger.logErrorEvent('continuous_listening_start_failed', error as Error, {}, this.userAge, this.config?.conversation_id);
      throw error;
    }
  }

  stopContinuousListening(): void {
    if (!this.isContinuousListening) {
      return;
    }

    if (this.continuousAudioRecorder) {
      this.continuousAudioRecorder.stop();
      this.continuousAudioRecorder = null;
    }

    this.isContinuousListening = false;
    
    logger.logAudioEvent('continuous_listening_stopped', {}, this.userAge, this.config?.conversation_id);
    this.emit('continuousListeningStateChange', false);
  }

  isContinuousListeningActive(): boolean {
    return this.isContinuousListening;
  }
  
  /**
   * Check if microphone is currently muted due to assistant speaking (consolidated)
   */
  isMicMutedForAssistant(): boolean {
    return this.micMutedForAssistant;
  }
  
  /**
   * @deprecated Use isMicMutedForAssistant() instead - kept for backwards compatibility
   */
  isMicMutedDueToAssistant(): boolean {
    return this.micMutedForAssistant;
  }

  toggleContinuousListening(): Promise<void> {
    if (this.isContinuousListening) {
      this.stopContinuousListening();
      return Promise.resolve();
    } else {
      return this.startContinuousListening();
    }
  }

  // Simplified announcement handling - server controls announcement behavior

  // Cleanup
  disconnect(): void {
    const conversationId = this.config?.conversation_id;
    
    // Stop continuous listening
    this.stopContinuousListening();
    
    // Clean up transcription tracking
    if (this.transcriptionTimeout) {
      clearTimeout(this.transcriptionTimeout);
      this.transcriptionTimeout = null;
    }
    this.awaitingUserTranscription = null;
    
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    this.connectionState = 'disconnected';
    this.conversationState = 'idle';
    this.config = null;
    this.reconnectAttempts = 0;
    
    logger.logConnectionEvent('disconnected_manually', {}, undefined, conversationId);
    this.emit('connectionStateChange', this.connectionState);
  }

  cleanup(): void {
    this.disconnect();
    this.eventListeners.clear();
  }
}